import { Flex, Form, Modal, Select, Typography } from "antd";
import { useForm } from "antd/es/form/Form";
import { ModalHeader } from "components/modals/ModalChrome";
import { useVoucherEligiblePackages } from "hooks/queries/loyalty";
import { useRedeemVoucherDiscount } from "hooks/mutations/loyalty";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { getAllPaymentMethods } from "hooks/queries/payment-methods";
import { translate } from "shared/languages";
import { CustomerLoyaltyVoucher } from "shared/types/loyalty";
import { TaxMode } from "shared/types/enum";
import { formatCurrency } from "shared/utils";
import { rewardLabel } from "shared/utils/loyalty";
import { applyPurchaseTax } from "shared/utils/tax";
import { DEFAULT_DATE_FORMAT, formatDate } from "shared/utils/dayjs";
import { colors } from "shared/theme/color";
import ReferralCompletesNote from "components/ReferralCompletesNote.component";
import { textPx } from "shared/constants/zoom";
import { InclusiveTaxNote } from "components/InclusiveTaxNote.component";

const { Text } = Typography;

interface RedeemDiscountVoucherModalProps {
  voucher: CustomerLoyaltyVoucher | null;
  open: boolean;
  onCancel: () => void;
  /** The client buying; only used to say when the purchase completes their
   *  pending referral. */
  customerId?: string;
}

// Purchase-style popup for a discount voucher: the package Select is
// restricted to the voucher's frozen scope (server-resolved, price rounding
// computed once server-side), with a price breakdown before confirming.
// Closes only on success (cf. BuyPackageModal). The caller wraps it in
// modalButtonsTheme.
const RedeemDiscountVoucherModal = ({
  voucher,
  open,
  onCancel,
  customerId,
}: RedeemDiscountVoucherModalProps) => {
  const [form] = useForm();
  const { data, isLoading } = useVoucherEligiblePackages(voucher?.id, open);
  const redeemDiscount = useRedeemVoucherDiscount();
  const { data: paymentMethods = [] } = getAllPaymentMethods();
  // This redemption goes through the same server-side purchase path as a
  // normal buy, so additive tax adds to discounted_price too — see
  // BuyPackageModal's identical preview for the reasoning.
  const { data: businessSettings } = useBusinessSettings();
  const isAdditiveTax =
    !!businessSettings?.tax_enabled &&
    businessSettings.tax_mode === TaxMode.Additive &&
    !!businessSettings.tax_rate;
  const withTax = (price: number) =>
    businessSettings ? applyPurchaseTax(price, businessSettings) : price;

  const packages = data?.packages ?? [];
  const options = packages.map((p) => ({
    value: p.package_id,
    label: p.product_name ? `${p.product_name} · ${p.name}` : p.name,
  }));

  const onSubmit = async () => {
    const v = await form.validateFields().catch(() => null);
    if (!v || !voucher) return;
    redeemDiscount.mutate(
      { voucherId: voucher.id, packageId: v.package_id, paymentMethodId: v.payment_method },
      {
        onSuccess: () => {
          onCancel();
          form.resetFields();
        },
      }
    );
  };

  return (
    <Modal
      open={open}
      onOk={onSubmit}
      onCancel={onCancel}
      okText={translate("loyalty.discount_modal.confirm")}
      cancelText={translate("button.cancel")}
      confirmLoading={redeemDiscount.isPending}
      afterClose={() => form.resetFields()}
      title={
        <ModalHeader
          eyebrow={voucher ? rewardLabel(voucher) : undefined}
          title={translate("loyalty.discount_modal.title")}
        />
      }
    >
      <Flex vertical gap={4} style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: textPx(13) }}>
          {translate("loyalty.discount_modal.subtitle")}
        </Text>
        {voucher && (
          <Text type="secondary" style={{ fontSize: textPx(13) }}>
            {translate("loyalty.claim.deadline")}{" "}
            <strong>{formatDate(voucher.expires_at, DEFAULT_DATE_FORMAT)}</strong>
          </Text>
        )}
      </Flex>
      <Form form={form} layout="vertical">
        <Form.Item
          name="package_id"
          label={translate("loyalty.discount_modal.package")}
          rules={[{ required: true, message: translate("form.error.required") }]}
        >
          <Select
            options={options}
            loading={isLoading}
            optionFilterProp="label"
            showSearch
            placeholder={translate("loyalty.discount_modal.package_placeholder")}
            notFoundContent={translate("loyalty.discount_modal.none_eligible")}
          />
        </Form.Item>
        {/* This is a real sale at the discounted price, so it records how the
            client paid, exactly like BuyPackageModal: optional, same place
            (after the package, before the breakdown). */}
        <Form.Item name="payment_method" label={translate("paymentMethods.name")}>
          <Select
            allowClear
            placeholder={translate("paymentMethods.select_placeholder")}
            options={paymentMethods.map((method) => ({ label: method.name, value: method.id }))}
          />
        </Form.Item>
        <Form.Item noStyle dependencies={["package_id"]}>
          {({ getFieldValue }) => {
            const selected = packages.find(
              (p) => p.package_id === getFieldValue("package_id")
            );
            if (!selected) return null;
            return (
              <Flex vertical gap={6}>
                <Text strong style={{ fontSize: textPx(13) }}>
                  {translate("label.price_breakdown")}
                </Text>
                <Flex justify="space-between">
                  <Text type="secondary">
                    {translate("loyalty.discount_modal.original_price")}
                  </Text>
                  <Text delete type="secondary" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatCurrency(selected.original_price)}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  {/* Label stays grey/secondary like every other component
                      row; the amount gets the same green "you saved money"
                      highlight as BuyPackageModal's discount row, for a
                      consistent look across both purchase modals. */}
                  <Text type="secondary">
                    {voucher ? rewardLabel(voucher) : ""}
                  </Text>
                  <Text style={{ fontVariantNumeric: "tabular-nums", color: colors.successDark }}>
                    −{" "}
                    {formatCurrency(
                      selected.original_price - selected.discounted_price
                    )}
                  </Text>
                </Flex>
                <Flex justify="space-between">
                  {/* Relabeled "Subtotal" once additive tax adds on top —
                      demoted to a regular-weight, grey component row in
                      that case (matching Original Price above), with
                      Pajak/Total added below as the actual bold black
                      final rows. Without tax, this genuinely IS the final
                      amount, so it keeps full bold/black treatment. */}
                  <Text
                    strong={!isAdditiveTax}
                    type={isAdditiveTax ? "secondary" : undefined}
                  >
                    {translate(isAdditiveTax ? "pdf.subtotal" : "loyalty.discount_modal.final_price")}
                  </Text>
                  <Text
                    strong={!isAdditiveTax}
                    type={isAdditiveTax ? "secondary" : undefined}
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatCurrency(selected.discounted_price)}
                  </Text>
                </Flex>
                <InclusiveTaxNote
                  amount={selected.discounted_price}
                  enabled={businessSettings?.tax_enabled}
                  mode={businessSettings?.tax_mode}
                  rate={businessSettings?.tax_rate}
                  label={businessSettings?.tax_label}
                />
                {isAdditiveTax && (
                  <>
                    <Flex justify="space-between">
                      <Text type="secondary">
                        {businessSettings?.tax_label} ({businessSettings?.tax_rate}%)
                      </Text>
                      <Text type="secondary" style={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatCurrency(
                          withTax(selected.discounted_price) - selected.discounted_price,
                        )}
                      </Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text strong>{translate("analyticsPage.class.total_label")}</Text>
                      <Text strong style={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatCurrency(withTax(selected.discounted_price))}
                      </Text>
                    </Flex>
                  </>
                )}
                {/* This purchase converts the client's pending referral (System
                    Review S19), and the intro discount can't stack with the
                    voucher, so it is issued as a voucher for later. */}
                <ReferralCompletesNote
                  customerId={customerId}
                  open={open}
                  basePrice={selected.original_price}
                  kind="purchase"
                  style={{ marginTop: 10 }}
                />
              </Flex>
            );
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RedeemDiscountVoucherModal;
